import { useEffect, useMemo, useState } from 'react';
import { api } from '../api';
import { COMPANY_STATUS, MEMBER_STATUS, ROLE_LABEL, TRIP_STATUS, dateOnly, dateTime, expiryBadge, fold, timeAgo, unitName } from '../format';
import type { AuditEvent, CompanyInfo, CompanyNote, Place, Session, Trip, TripDoc, TripDocKind } from '../types';
import { TRIP_DOC_KINDS } from '../types';
import { Badge, Btn, Card, CardTitle, Empty, Field, Icon, Loading, Modal, Notice, SecHead, Select, Skeleton, Stat, TextArea } from '../components/ui';
import { AuditRow, auditSubject } from './Audit';
import { CodeModal, LeaveCompanyButton } from './Company';
import { TripForm, TripStops, tripProgressBadge, type TripFormValue } from './TripForm';
import { UnitForm, UnitsCard } from './Units';
import { DocFilesField, DocumentsCard, MemberDocsModal, TripDocAttachment, useDocFiles } from './Documents';
import { usePortal } from '../App';
import { useDensity } from '../useDensity';

/** Filtros de «Actividad reciente»: el prefijo de `action` agrupa el evento;
    lo que no cuadra con un filtro (sesiones, lugares del directorio,
    visitas, aportaciones…) solo aparece en «Todo». */
type FeedFilter = 'all' | 'trips' | 'units' | 'docs' | 'team';
const FEED_FILTERS: { id: FeedFilter; label: string; empty: string }[] = [
  { id: 'all', label: 'Todo', empty: 'la empresa' },
  { id: 'trips', label: 'Viajes', empty: 'viajes' },
  { id: 'units', label: 'Unidades', empty: 'unidades' },
  { id: 'docs', label: 'Documentos', empty: 'documentos' },
  { id: 'team', label: 'Equipo', empty: 'el equipo' },
];
function auditFeedGroup(action: string): Exclude<FeedFilter, 'all'> | null {
  if (action.startsWith('trip.') || action.startsWith('tripDoc.')) return 'trips';
  if (action.startsWith('unit.') || action.startsWith('unitReport.') || action.startsWith('meter.') || action.startsWith('unitInspection')) return 'units';
  if (action.startsWith('doc.')) return 'docs';
  if (action.startsWith('company.') || action.startsWith('member.') || action.startsWith('membership.')) return 'team';
  return null;
}

/** Vista de la oficina de una empresa, por secciones del riel: Tablero
    (conteos y resumen), Viajes, Unidades, Documentos, Equipo, Lugares
    (privados/frecuentes/notas) y Config (código de invitación, datos). */
export function OfficeView({ section, go }: { section: string; go(id: string, extra?: Record<string, string>): void }) {
  const { token, session, data, busy, mutate, refresh, authFail } = usePortal();
  const [assigning, setAssigning] = useState(false);
  const [editing, setEditing] = useState<Trip | null>(null);
  const [addingUnit, setAddingUnit] = useState(false);
  const [codeShown, setCodeShown] = useState<string | null>(null);
  const [memberDocsFor, setMemberDocsFor] = useState<string | null>(null);
  // Deep-links por URL (sobreviven al remonte .view, refresh y se comparten):
  // ?v=unidades&u=<unitId> abre la ficha; ?v=viajes&t=<tripId> el detalle.
  // La vista los lee en el estado inicial y los limpia al consumirlos.
  const urlParam = (k: string) => new URLSearchParams(window.location.search).get(k);
  const [viewTrip, setViewTrip] = useState<string | null>(() => urlParam('t'));
  const [authorizing, setAuthorizing] = useState<Trip | null>(null);
  // Densidad «Cómoda / Compacta» de las listas de viajes y equipo (misma
  // preferencia que Unidades y Documentos — useDensity).
  const { dense, seg: densitySeg } = useDensity();

  // Mini-bitácora del tablero (listAudit): los últimos 30 eventos de la
  // empresa, carga perezosa la primera vez que se entra a «tablero» — igual
  // que la bitácora de la ficha de unidad (Units).
  const [audit, setAudit] = useState<AuditEvent[] | null>(null);
  const [auditFailed, setAuditFailed] = useState(false);
  const [feedFilter, setFeedFilter] = useState<FeedFilter>('all');
  // Deep-link del feed: tocar una fila de unidad lleva ?v=unidades&u=<id> y
  // UnitsCard abre su ficha (consume el id con onOpened); una de viaje lleva
  // ?v=viajes&t=<id> que viewTrip ya leyó en su estado inicial.
  const [openUnitId, setOpenUnitId] = useState<string | null>(() => urlParam('u'));
  // El param refleja el estado en ambos sentidos: abierto → ?t=/?u= en la
  // URL (recargar o compartir reabre), cerrado → fuera. replaceState, así
  // que no ensucia el historial de atrás/adelante.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const had = params.get('t');
    if (viewTrip) params.set('t', viewTrip); else params.delete('t');
    if (params.get('t') !== had) window.history.replaceState(null, '', `${window.location.pathname}?${params.toString()}`);
  }, [viewTrip]);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const had = params.get('u');
    if (openUnitId) params.set('u', openUnitId); else params.delete('u');
    if (params.get('u') !== had) window.history.replaceState(null, '', `${window.location.pathname}?${params.toString()}`);
  }, [openUnitId]);
  const loadAudit = async () => {
    try {
      const { entries } = await api<{ entries: AuditEvent[] }>('listAudit', {}, token);
      setAudit(entries ?? []); setAuditFailed(false);
    } catch (e) { if (authFail(e)) return; setAudit(null); setAuditFailed(true); }
  };
  // `data` en deps: cambia de identidad tras cada mutate/refresh exitoso, así
  // el feed se recarga solo cuando el snapshot se actualiza (asignar viaje,
  // verificar doc…). Tras un fallo manual se queda en «Reintentar» — no se
  // reintenta solo para no martillear un servicio caído.
  useEffect(() => {
    if (section === 'tablero' && data?.company?.id && !auditFailed) void loadAudit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, section, data?.company?.id, data]);

  const places = useMemo(() => [...(data?.places ?? []), ...(data?.companyPlaces ?? [])], [data]);
  const placeName = useMemo(() => {
    const map = new Map(places.map(p => [p.id, p]));
    return (id: string) => map.get(id)?.name ?? 'Lugar no disponible';
  }, [places]);

  if (!data) return <Loading label="Leyendo tu empresa…" />;

  const company = data.company;
  const companyName = company?.name ?? session.companyName ?? 'tu empresa';
  // La oficina primero y luego por nombre (mismo orden que «Mi empresa» de la app).
  const members = data.companyMembers.slice().sort((a, b) =>
    Number(b.companyRole === 'office') - Number(a.companyRole === 'office') || a.displayName.localeCompare(b.displayName, 'es'));
  const officeCount = members.filter(m => m.companyRole === 'office').length;
  // Para asignar viajes solo cuentan choferes activos: el backend
  // rechaza (CONFLICT) un viaje a quien está pendiente o suspendido.
  const drivers = members.filter(m => m.companyRole !== 'office' && m.status === 'active');
  const openTrips = (data.companyTrips ?? []).filter(t => t.status === 'assigned' || t.status === 'active').sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  const closedTrips = (data.companyTrips ?? []).filter(t => t.status === 'done' || t.status === 'cancelled').sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
  const st = company ? COMPANY_STATUS[company.status] : null;
  const canDispatch = !!company && st?.tone === 'ok';

  // Flota y documentos del snapshot (llegan con el backend nuevo; sin él, []).
  const units = data.companyUnits ?? [];
  const docs = data.companyDocuments ?? [];
  const unitById = new Map(units.map(u => [u.id, u]));
  const unitLabel = (id?: string | null) => {
    if (!id) return null;
    const u = unitById.get(id);
    return u ? unitName(u, company) : 'Unidad';
  };
  const tripUnits = (trip: Trip) => [unitLabel(trip.tractorUnitId), unitLabel(trip.trailerUnitId)].filter(Boolean).join(' + ');

  // Nombres para la mini-bitácora del tablero: el sujeto de cada evento se
  // resuelve contra el snapshot (unidades, miembros, viajes, documentos,
  // reportes, llantas y lugares) — auditSubject arma «Tracto R-01»,
  // «Seguro (México) — Tracto R-01» o «María · Central → Norte».
  const memberById = new Map(members.map(m => [m.uid, m]));
  const tripById = new Map((data.companyTrips ?? []).map(t => [t.id, t]));
  const actorName = (uid: string) => { const m = memberById.get(uid); return m ? (m.displayName || m.email) : 'la oficina'; };
  const resolveName = (uid: string) => memberById.get(uid)?.displayName || memberById.get(uid)?.email || undefined;
  const auditSubjects = {
    units: unitById,
    members: memberById,
    trips: tripById,
    docs: new Map(docs.map(d => [d.id, d])),
    reports: new Map((data.companyUnitReports ?? []).map(r => [r.id, r])),
    tires: new Map((data.companyUnitTires ?? []).map(t => [t.id, t])),
    company,
    placeName,
  };

  // Feed del tablero filtrado por tema (FEED_FILTERS) y sus deep-links:
  // a unidad → go('unidades') + ficha abierta; a viaje → go('viajes') +
  // la ventana de detalle que ya maneja viewTrip.
  const openUnit = (id: string) => go('unidades', { u: id });
  const openTrip = (id: string) => go('viajes', { t: id });
  const feed = (audit ?? []).filter(e => feedFilter === 'all' || auditFeedGroup(e.action) === feedFilter);
  const feedSeg = (
    <div className="seg" role="group" aria-label="Filtrar la actividad">
      {FEED_FILTERS.map(f => <button key={f.id} type="button"
        className={`seg-btn${feedFilter === f.id ? ' on' : ''}`} aria-pressed={feedFilter === f.id}
        onClick={() => setFeedFilter(f.id)}>{f.label}</button>)}
    </div>
  );

  // Defectos graves abiertos por unidad (reportes high sin resolver): con
  // uno el chofer no puede arrancar el viaje — la oficina puede autorizar
  // la salida y queda asentada en el viaje (defectOverride).
  const highOpenByUnit = new Map<string, number>();
  for (const r of data.companyUnitReports ?? []) {
    if (r.severity === 'high' && (r.status === 'open' || r.status === 'inShop'))
      highOpenByUnit.set(r.unitId, (highOpenByUnit.get(r.unitId) ?? 0) + 1);
  }
  // Por viaje: total de defectos graves y las unidades que los traen.
  const tripDefects = (trip: Trip): { total: number; names: string[] } => {
    let total = 0;
    const names: string[] = [];
    for (const id of [trip.tractorUnitId, trip.trailerUnitId]) {
      const n = id ? highOpenByUnit.get(id) ?? 0 : 0;
      if (n) { total += n; names.push(unitLabel(id) ?? 'Unidad'); }
    }
    return { total, names };
  };

  // Avisos del tablero: documentos pendientes de verificar, vencidos/por vencer
  // (ventana de 30 días) y unidades fuera de circulación.
  const pendingDocs = docs.filter(d => d.status === 'pendingReview' && !d.supersededBy).length;
  const expiringDocs = docs.filter(d => !d.supersededBy && d.status !== 'rejected' && expiryBadge(d.expiresAt)).length;
  const shopUnits = units.filter(u => u.status === 'shop').length;
  const downUnits = units.filter(u => u.status === 'down').length;
  const activeUnits = units.filter(u => u.status === 'active').length;

  // Saludo contextual del tablero: saluda a la persona (el título ya nombra
  // la empresa) según la hora local; sin displayName la descripción va sola.
  const firstName = (session.displayName || '').trim().split(/\s+/)[0];
  const hour = new Date().getHours();
  const greeting = firstName
    ? `${hour < 12 ? 'Buenos días' : hour < 19 ? 'Buenas tardes' : 'Buenas noches'}, ${firstName}. `
    : '';

  const submitAssign = async (value: TripFormValue) => {
    // `units` solo viaja cuando se eligió al menos una — un backend sin el
    // módulo rechazaría la llave extra (whitelist de payload) y el viaje no
    // debe fallar por algo opcional.
    const units = (value.tractorUnitId || value.trailerUnitId)
      ? { tractorUnitId: value.tractorUnitId, trailerUnitId: value.trailerUnitId }
      : undefined;
    if (await mutate(() => api('assignTrip', { driverId: value.driverId, stops: value.stops, notes: value.notes, ...(units ? { units } : {}) }, token), 'Viaje asignado.')) setAssigning(false);
  };
  const submitEdit = async (trip: Trip, value: TripFormValue) => {
    // Las unidades viajan en el patch plano solo cuando cambian — así editar
    // paradas/notas sigue funcionando en un backend sin el módulo.
    const payload: Record<string, unknown> = { tripId: trip.id, stops: value.stops, notes: value.notes };
    if ((value.tractorUnitId ?? null) !== (trip.tractorUnitId ?? null)) payload.tractorUnitId = value.tractorUnitId;
    if ((value.trailerUnitId ?? null) !== (trip.trailerUnitId ?? null)) payload.trailerUnitId = value.trailerUnitId;
    if (await mutate(() => api('updateTrip', payload, token), 'Viaje actualizado.')) setEditing(null);
  };
  const cancelTrip = (trip: Trip) => {
    if (!confirm(`¿Cancelar el viaje de ${trip.driverName}? El chofer lo verá como cancelado.`)) return;
    void mutate(() => api('setTripStatus', { tripId: trip.id, status: 'cancelled' }, token), 'Viaje cancelado.');
  };
  const rotate = () => {
    if (!company) return;
    if (!confirm('¿Generar un código de invitación nuevo? El anterior dejará de funcionar.')) return;
    void mutate(async () => {
      const { code } = await api<{ code: string }>('rotateCompanyCode', { companyId: company.id }, token);
      setCodeShown(code);
    });
  };
  const changeRole = (m: Session) => {
    const name = m.displayName || 'este miembro';
    const toOffice = m.companyRole !== 'office';
    if (!confirm(toOffice
      ? `¿Pasar a ${name} a la oficina? Podrá administrar la empresa y despachar viajes.`
      : `¿Pasar a ${name} a chofer? Dejará de administrar la empresa: no podrá despachar viajes ni gestionar miembros.`)) return;
    // Si era la última persona de oficina el backend responde CONFLICT y el
    // banner muestra «Debe quedar alguien en la oficina.»
    void mutate(() => api('setCompanyRole', { uid: m.uid, role: toOffice ? 'office' : 'driver' }, token), 'Rol de empresa actualizado.');
  };
  const removeMember = (m: Session) => {
    const name = m.displayName || 'este miembro';
    const extra = m.companyRole === 'office' && officeCount <= 1
      ? ' Es la única persona en la oficina: la empresa quedará sin quien la administre.'
      : '';
    if (!confirm(`¿Quitar a ${name} de ${companyName}? Conserva su acceso al directorio, pero pierde los datos privados de la empresa: lugares, notas y viajes.${extra}`)) return;
    void mutate(() => api('removeCompanyMember', { uid: m.uid }, token), 'El miembro salió de la empresa.');
  };

  // Avisos de estado de la empresa: se repiten en Tablero y Viajes porque son
  // las secciones donde bloquean el despacho.
  const gateNotices = <>
    {!company && <Notice tone="warning">Tu empresa aún no está activa o no perteneces a una. Pide al administrador del directorio que la autorice.</Notice>}
    {company && st && st.tone !== 'ok' && <Notice tone="warning">La empresa está {st.label.toLowerCase()} — mientras tanto no puedes despachar viajes.</Notice>}
  </>;

  const refreshBtn = <Btn tone="secondary" small busy={busy} onClick={() => void refresh()}>Actualizar</Btn>;
  const assignBtn = <Btn small busy={busy} disabled={!canDispatch || drivers.length === 0} onClick={() => setAssigning(true)}>Asignar viaje</Btn>;

  return <>
    {section === 'tablero' && <>
      <SecHead title={company?.name ?? 'Mi empresa'} desc={`${greeting}Lo que pasa ahora en tu operación, de un vistazo.`} actions={<>{refreshBtn}{assignBtn}</>} />
      {gateNotices}
      {/* Banner de conteos: documentos que piden verificación o están por
          vencer, y unidades en taller/fuera de servicio. */}
      {(pendingDocs + expiringDocs + shopUnits + downUnits) > 0 && <Notice tone="warning">
        {[
          pendingDocs ? `${pendingDocs} documento${pendingDocs === 1 ? '' : 's'} por verificar` : '',
          expiringDocs ? `${expiringDocs} documento${expiringDocs === 1 ? '' : 's'} vencido${expiringDocs === 1 ? '' : 's'} o por vencer` : '',
          shopUnits ? `${shopUnits} unidad${shopUnits === 1 ? '' : 'es'} en taller` : '',
          downUnits ? `${downUnits} unidad${downUnits === 1 ? '' : 'es'} fuera de servicio` : '',
        ].filter(Boolean).join(' · ')}
      </Notice>}

      {/* KPIs en una sola fila a lo ancho: icono + etiqueta small-caps +
          cifra tabular + pista. Debajo de ~1250 px reenvuelven a rejilla. */}
      <div className="kpis">
        <Stat icon="route" n={openTrips.length} l="Viajes abiertos" hint="Asignados o en curso" />
        <Stat icon="person" n={drivers.length} l="Choferes activos" hint="Pueden recibir viajes" />
        <Stat icon="people" n={members.length} l="Miembros" hint={`${officeCount} en la oficina`} />
        <Stat icon="truck" n={activeUnits} l="Unidades activas" hint={units.length ? `De ${units.length} en la flota` : undefined} />
        <Stat icon="doc" n={pendingDocs} l="Por verificar" warn={pendingDocs > 0} hint="Docs que sube el equipo" />
        <Stat icon="clock" n={expiringDocs} l="Por vencer" warn={expiringDocs > 0} hint="Ventana de 30 días" />
        <Stat icon="pulse" n={shopUnits + downUnits} l="Detenidas" warn={shopUnits + downUnits > 0} hint="Taller o fuera de servicio" />
      </div>

      {/* Acciones rápidas: la que usa la oficina todos los días. */}
      <div className="qgrid">
        <button type="button" className="qaction" disabled={!canDispatch || drivers.length === 0} onClick={() => setAssigning(true)}>
          <Icon name="route" size={18} /><span className="grow">Asignar viaje</span><Icon name="chevronRight" size={15} />
        </button>
        {/* Viajes lleva conteo de abiertos (badge neutro: trabajo en curso,
            no alerta como el warn de Documentos/Unidades). */}
        <button type="button" className="qaction" onClick={() => go('viajes')}>
          <Icon name="route" size={18} /><span className="grow">Viajes</span>
          {openTrips.length > 0 ? <Badge tone="muted">{openTrips.length}</Badge> : <Icon name="chevronRight" size={15} />}
        </button>
        <button type="button" className="qaction" onClick={() => go('documentos')}>
          <Icon name="doc" size={18} /><span className="grow">Documentos</span>
          {pendingDocs + expiringDocs > 0 ? <Badge tone="warn">{pendingDocs + expiringDocs}</Badge> : <Icon name="chevronRight" size={15} />}
        </button>
        <button type="button" className="qaction" onClick={() => go('unidades')}>
          <Icon name="truck" size={18} /><span className="grow">Unidades</span>
          {shopUnits + downUnits > 0 ? <Badge tone="warn">{shopUnits + downUnits}</Badge> : <Icon name="chevronRight" size={15} />}
        </button>
        <button type="button" className="qaction" onClick={() => go('equipo')}>
          <Icon name="people" size={18} /><span className="grow">Equipo</span><Icon name="chevronRight" size={15} />
        </button>
      </div>

      <Card>
        <div className="row between multiline">
          <CardTitle detail="Viajes asignados o en curso ahora mismo.">Actividad de viajes{openTrips.length ? ` · ${openTrips.length}` : ''}</CardTitle>
          {openTrips.length > 0 && <Btn small tone="ghost" onClick={() => go('viajes')}>Ver todos</Btn>}
        </div>
        {!openTrips.length && <Empty icon="route" title="Sin viajes"
          message={drivers.length ? 'Asigna el primero: chofer, paradas y carga.' : 'Necesitas al menos un chofer activo en la empresa para asignar viajes.'}
          action={canDispatch && drivers.length > 0 ? assignBtn : undefined} />}
        {openTrips.length > 0 && <div className="alist">
          {openTrips.slice(0, 5).map(trip => {
            // Estado inesperado del backend → etiqueta cruda en gris, nunca undefined.
            const ts = TRIP_STATUS[trip.status] ?? { label: trip.status, tone: 'muted' as const };
            const total = trip.stops.length;
            const done = trip.stops.filter(s => s.progress?.status === 'done').length;
            const visiting = trip.stops.find(s => s.progress?.status === 'visiting');
            const defs = tripDefects(trip);
            return <button type="button" className="arow" key={trip.id} onClick={() => go('viajes')}>
              <span className="acol">
                <span className="title">{trip.driverName || 'Chofer'}</span>
                <span className="small muted">{done} de {total} parada{total === 1 ? '' : 's'} visitada{done === 1 ? '' : 's'}{visiting ? ` · En ${placeName(visiting.placeId)} ahora` : ''}</span>
                {defs.total > 0 && !trip.defectOverride && <span className="tiny u-bad">{defs.total} defecto{defs.total === 1 ? '' : 's'} grave{defs.total === 1 ? '' : 's'} sin resolver — {defs.names.join(', ')}</span>}
                {defs.total > 0 && !trip.defectOverride && trip.defectAuthRequest && <span className="tiny u-warn">El chofer pide tu autorización para salir</span>}
                {trip.defectOverride && <span className="tiny u-ok">Salida con defectos autorizada por {trip.defectOverride.byName || 'la oficina'}</span>}
                <span className="progress" role="progressbar" aria-valuemin={0} aria-valuemax={total} aria-valuenow={done} aria-label="Paradas visitadas">
                  <span className="fill" style={{ width: `${total ? Math.round(done / total * 100) : 0}%` }} />
                </span>
              </span>
              <span className="route ellipsis">{trip.stops.map(s => placeName(s.placeId)).join(' → ')}</span>
              <Badge tone={ts.tone}>{ts.label}</Badge>
              <Icon name="chevronRight" size={16} />
            </button>;
          })}
        </div>}
        {openTrips.length > 5 && <div className="actions"><Btn small tone="ghost" onClick={() => go('viajes')}>Ver los {openTrips.length} viajes</Btn></div>}
      </Card>

      {/* Mini-bitácora de la empresa (listAudit): los últimos movimientos de
          unidades, viajes, documentos y equipo — carga perezosa al entrar al
          tablero. Sin «ver toda»: el detalle completo ya vive en la ficha de
          cada unidad (pestaña Bitácora). Las filas con sujeto resoluble
          navegan a su ficha (unidad o viaje). */}
      {company && <Card>
        <CardTitle detail="Últimos movimientos de la empresa: unidades, viajes, documentos y equipo.">Actividad reciente</CardTitle>
        {/* Barra de filtros por tema (misma convención .u-toolbar que la
            densidad de Viajes/Unidades): seg a la izquierda, conteo de los
            movimientos del filtro a la derecha. */}
        {audit !== null && audit.length > 0 && <div className="u-toolbar">
          {feedSeg}
          {/* Solo se renderizan 8: el conteo dice cuántos se ven del total. */}
          <span className="tiny muted">{feed.length > 8 ? `8 de ${feed.length}` : feed.length} {feed.length === 1 ? 'movimiento' : 'movimientos'}</span>
        </div>}
        {audit === null && !auditFailed && <Skeleton rows={4} />}
        {/* Mismo aire que <Notice tone="warning"> pero sin role="status": una
            región viva no debe contener controles (el botón Reintentar). */}
        {auditFailed && <div className="notice warning"><span className="nbody">No se pudo cargar la actividad reciente. <Btn small tone="ghost" onClick={() => { setAuditFailed(false); void loadAudit(); }}>Reintentar</Btn></span></div>}
        {audit !== null && !audit.length && <Empty icon="list" title="Sin actividad todavía" message="Los viajes asignados, los cambios de las unidades, los documentos y los movimientos del equipo aparecerán aquí." />}
        {audit !== null && audit.length > 0 && feed.length > 0 && <div className="utl">
          {feed.slice(0, 8).map(e => {
            const subj = auditSubject(e, auditSubjects);
            // Deep-link a la ficha del sujeto: viaje si la acción es de viaje
            // y sigue en el snapshot; si no, la unidad resuelta — subjectId
            // directo, la de auditSubject (reporte/llanta) o el unitIds[]
            // que serializa el backend nuevo.
            const tripId = (e.action.startsWith('trip.') || e.action.startsWith('tripDoc.'))
              ? (tripById.has(e.subjectId) ? e.subjectId : e.tripId && tripById.has(e.tripId) ? e.tripId : undefined)
              : undefined;
            const unitId = unitById.has(e.subjectId) ? e.subjectId
              : (e.unitIds ?? []).find(id => unitById.has(id)) ?? subj.unit?.id;
            const onClick = tripId ? () => openTrip(tripId) : unitId ? () => openUnit(unitId) : undefined;
            return <AuditRow key={e.id} entry={e} subject={subj.text} unit={subj.unit} company={company} memberName={actorName} resolveName={resolveName} onClick={onClick} />;
          })}
        </div>}
        {audit !== null && audit.length > 0 && !feed.length &&
          <div className="small muted">Sin movimientos de {FEED_FILTERS.find(f => f.id === feedFilter)?.empty ?? 'la empresa'}.</div>}
      </Card>}
    </>}

    {section === 'viajes' && <>
      <SecHead title="Viajes" desc="Los viajes llegan a la app del chofer asignado con sus paradas y carga." actions={<>{refreshBtn}{assignBtn}</>} />
      {gateNotices}
      {/* Barra de la lista: densidad (persistida, compartida con Unidades) a
          la izquierda y conteo de abiertos a la derecha — convención de la
          .u-toolbar de Unidades. Solo aparece cuando hay lista que densificar. */}
      {(openTrips.length > 0 || closedTrips.length > 0) && <div className="u-toolbar">
        {densitySeg}
        <span className="tiny muted">{openTrips.length} {openTrips.length === 1 ? 'viaje abierto' : 'viajes abiertos'}</span>
      </div>}
      <Card className={dense ? 'dense' : undefined}>
        {!openTrips.length && <Empty icon="route" title="Sin viajes" message={drivers.length ? 'Asigna el primero con el botón de arriba: chofer, paradas y carga.' : 'Necesitas al menos un chofer activo en la empresa para asignar viajes.'}
          action={canDispatch && drivers.length > 0 ? assignBtn : undefined} />}
        <div className="alist">
          {openTrips.map(trip => <TripRow key={trip.id} trip={trip} placeName={placeName} defects={tripDefects(trip)} onOpen={() => setViewTrip(trip.id)} />)}
        </div>
      </Card>

      {closedTrips.length > 0 && <details className={`card flat collapsible${dense ? ' dense' : ''}`}>
        <summary className="card-title">Viajes cerrados · {closedTrips.length}</summary>
        <div className="alist">
          {closedTrips.map(trip => <TripRow key={trip.id} trip={trip} placeName={placeName} defects={tripDefects(trip)} onOpen={() => setViewTrip(trip.id)} />)}
        </div>
      </details>}

      {viewTrip && (() => {
        const t = (data.companyTrips ?? []).find(x => x.id === viewTrip);
        return <TripDetailModal
          trip={t}
          placeName={placeName} unitsText={id => tripUnits(id)} defects={t ? tripDefects(t) : { total: 0, names: [] }}
          onEdit={x => { setViewTrip(null); setEditing(x); }}
          onCancel={cancelTrip}
          onAuthorize={x => { setViewTrip(null); setAuthorizing(x); }}
          onClose={() => setViewTrip(null)} />;
      })()}
    </>}

    {section === 'unidades' && <>
      {/* El alta vive en el encabezado de sección: la tarjeta no repite título. */}
      <SecHead
        title={<>Unidades{units.length > 0 && <span className="cnt"> · {units.length}</span>}</>}
        desc="Vehículos y remolques de la empresa — toca una tarjeta para ver su ficha: contador, mantenimiento, equipo y documentos."
        actions={<>
          {refreshBtn}
          <Btn small busy={busy} disabled={!company} onClick={() => setAddingUnit(true)}><Icon name="plus" size={15} />Agregar unidad</Btn>
        </>} />
      {!company && gateNotices}
      {company && <UnitsCard units={units} docs={docs} members={members} onAdd={() => setAddingUnit(true)}
        openUnitId={openUnitId} onOpened={() => setOpenUnitId(null)} />}
      {addingUnit && <UnitForm units={units} onClose={() => setAddingUnit(false)} />}
    </>}

    {section === 'documentos' && <>
      <SecHead title="Documentos" desc="Documentos con vencimiento de unidades y operadores — lo que sube un chofer espera tu verificación." actions={refreshBtn} />
      {!company && gateNotices}
      {company && <DocumentsCard docs={docs} units={units} members={members} />}
    </>}

    {section === 'equipo' && <>
      <SecHead
        title={<>Equipo{members.length > 0 && <span className="cnt"> · {members.length}</span>}</>}
        desc="Compañeros registrados con el código de la empresa — la oficina puede cambiar roles o quitar miembros."
        actions={refreshBtn} />
      {/* Misma barra de densidad que Viajes/Unidades: las filas .item ya
          tienen reglas .dense. Solo con lista que densificar. */}
      {members.length > 0 && <div className="u-toolbar">
        {densitySeg}
        <span className="tiny muted">{members.length} {members.length === 1 ? 'miembro' : 'miembros'} · {officeCount} en la oficina</span>
      </div>}
      <Card className={dense ? 'dense' : undefined}>
        {!members.length && <Empty icon="people" title="Sin miembros" message={company && st?.tone !== 'ok' ? 'Los compañeros aparecerán aquí cuando la empresa quede autorizada.' : 'Comparte el código de invitación para que se registren.'} />}
        <div className="list">
          {members.map(m => {
            const ms = MEMBER_STATUS[m.status] ?? { label: m.status, tone: 'muted' as const };
            const self = m.uid === session.uid;
            const isOffice = m.companyRole === 'office';
            return <div className="item" key={m.uid}>
              <div className="row between multiline">
                <div>
                  <div className="title">{m.displayName || 'Sin nombre'}{self ? ' (tú)' : ''}</div>
                  <div className="small muted">{m.email}{m.phone ? ` · ${m.phone}` : ''}</div>
                </div>
                <div className="actions">
                  <Badge tone={isOffice ? 'info' : 'muted'}>{ROLE_LABEL[isOffice ? 'office' : 'driver']}</Badge>
                  <Badge tone={ms.tone}>{ms.label}</Badge>
                </div>
              </div>
              <div className="actions">
                <Btn small tone="ghost" busy={busy} onClick={() => setMemberDocsFor(m.uid)}>Documentos y unidad habitual</Btn>
                {!self && <>
                  <Btn small tone="secondary" busy={busy} onClick={() => changeRole(m)}>{isOffice ? 'Pasar a chofer' : 'Pasar a oficina'}</Btn>
                  <Btn small tone="danger" busy={busy} onClick={() => removeMember(m)}>Quitar de la empresa</Btn>
                </>}
              </div>
            </div>;
          })}
        </div>
      </Card>
    </>}

    {section === 'lugares' && <>
      <SecHead title="Lugares" desc="Privados de la empresa, frecuentes del directorio y sus notas." />
      {!company && gateNotices}
      {company && <PrivatePlacesCard places={data.companyPlaces ?? []} />}
      {company && <CompanyPlacesCard company={company} places={data.places} favoriteIds={data.companyFavoriteIds ?? []} />}
    </>}

    {section === 'config' && <>
      <SecHead title="Configuración" desc="Datos de la empresa y su código de invitación." />
      {gateNotices}
      <Card>
        <CardTitle detail="La oficina despacha viajes y cuida los datos privados de la empresa.">Tu empresa</CardTitle>
        {company ? <dl className="kv">
          <dt>Nombre</dt><dd>{company.name}</dd>
          <dt>Estado</dt><dd><Badge tone={st?.tone ?? 'muted'}>{st?.label ?? company.status}</Badge></dd>
          <dt>Tu rol</dt><dd>Oficina</dd>
        </dl> : <Empty icon="building" title="Sin empresa activa" />}
        {/* Si la empresa fue borrada a mano el doc ya no llega pero la sesión
            conserva el companyId colgado: leaveCompany lo limpia (idempotente). */}
        {(company || session.companyId) && <div className="actions">
          {company && <Btn small tone="ghost" busy={busy} onClick={rotate}>Generar código de invitación nuevo</Btn>}
          <LeaveCompanyButton companyName={companyName} lastOffice={!!company && officeCount <= 1} />
        </div>}
      </Card>
    </>}

    {assigning && <TripForm
      title="Asignar viaje" submitLabel="Asignar viaje" busy={busy}
      places={places} favoriteIds={data.companyFavoriteIds} units={units} company={company}
      drivers={drivers.map(d => ({ uid: d.uid, displayName: d.displayName, defaultTractorId: d.defaultTractorId ?? null, defaultTrailerId: d.defaultTrailerId ?? null }))}
      initial={{ driverId: '', stops: [], notes: '', tractorUnitId: null, trailerUnitId: null }}
      onSubmit={submitAssign} onClose={() => setAssigning(false)} />}

    {editing && <TripForm
      title="Editar viaje" submitLabel="Guardar cambios" busy={busy}
      places={places} favoriteIds={data.companyFavoriteIds} units={units} company={company} drivers={[]} fixedDriverName={editing.driverName}
      initial={{ driverId: editing.driverId, stops: editing.stops, notes: editing.notes, tractorUnitId: editing.tractorUnitId ?? null, trailerUnitId: editing.trailerUnitId ?? null }}
      onSubmit={value => submitEdit(editing, value)} onClose={() => setEditing(null)} />}

    {memberDocsFor && (() => {
      const member = members.find(m => m.uid === memberDocsFor);
      return member ? <MemberDocsModal member={member} units={units} docs={docs} onClose={() => setMemberDocsFor(null)} /> : null;
    })()}

    {authorizing && <AuthorizeDefectsModal trip={authorizing} onClose={() => setAuthorizing(null)} />}

    {codeShown && <CodeModal title="Código de invitación nuevo" code={codeShown} onClose={() => setCodeShown(null)}>
      <p className="muted small u-m0">Compártelo con tus choferes; <b>se muestra una sola vez</b> y el anterior dejó de funcionar.</p>
    </CodeModal>}
  </>;
}

/** Lugares creados por la propia empresa (companyPlaces del snapshot): privados
    del equipo, se consultan aquí; el alta y la edición siguen en la app. */
function PrivatePlacesCard({ places }: { places: Place[] }) {
  return <Card>
    <CardTitle detail="Lugares que creó tu empresa: solo los ven sus miembros, no salen en el directorio general.">Lugares privados{places.length ? ` · ${places.length}` : ''}</CardTitle>
    {!places.length && <Empty icon="pin" title="Sin lugares privados" message="Se crean desde la app con la sesión de tu empresa; aquí se consultan." />}
    {places.length > 0 && <div className="list">
      {places.map(p => <div className="item" key={p.id}>
        <div className="row between multiline">
          <div>
            <div className="title">{p.name}</div>
            <div className="small muted">{[p.zone, p.country].filter(Boolean).join(', ')}</div>
            {p.address && <div className="tiny muted">{p.address}</div>}
          </div>
          <Badge tone="muted">Privado</Badge>
        </div>
      </div>)}
    </div>}
  </Card>;
}

const SEARCH_RESULTS_MAX = 8;

/** «Lugares de la empresa»: los lugares del directorio que la oficina marcó
    como frecuentes (companyFavoriteIds del snapshot). Salen primero al armar
    viajes (TripForm los agrupa) y cada uno carga las notas privadas del equipo.
    Solo operan con la empresa autorizada: pendiente/suspendida muestra aviso
    como el resto de la vista. */
function CompanyPlacesCard({ company, places, favoriteIds }: { company: CompanyInfo; places: Place[]; favoriteIds: string[] }) {
  const { token, busy, mutate } = usePortal();
  const [query, setQuery] = useState('');
  const [notesFor, setNotesFor] = useState<Place | null>(null);
  const active = company.status === 'active';
  const byId = useMemo(() => new Map(places.map(p => [p.id, p])), [places]);
  // Si un lugar salió del directorio su id queda sin resolver: se ignora en
  // vez de mostrar una fila rota.
  const favorites = useMemo(() => [...new Set(favoriteIds)].map(id => byId.get(id)).filter((p): p is Place => !!p), [favoriteIds, byId]);
  const q = fold(query.trim());
  const results = useMemo(() => {
    if (!q) return [] as Place[];
    const hits: Place[] = [];
    for (const p of places) {
      if (fold(`${p.name} ${p.zone} ${p.address}`).includes(q)) { hits.push(p); if (hits.length >= SEARCH_RESULTS_MAX) break; }
    }
    return hits;
  }, [q, places]);

  const setFavorite = (placeId: string, favorite: boolean) => {
    void mutate(() => api('setCompanyFavorite', { placeId, favorite }, token), favorite ? 'Lugar marcado como frecuente.' : 'Lugar quitado de los frecuentes.');
  };

  return <Card>
    <CardTitle detail="Los frecuentes salen primero al asignar viajes y guardan las notas privadas del equipo (claves, contactos internos).">Lugares frecuentes{favorites.length ? ` · ${favorites.length}` : ''}</CardTitle>
    {!active && <Notice tone="warning">La empresa está {(COMPANY_STATUS[company.status]?.label ?? company.status).toLowerCase()} — los lugares frecuentes y sus notas se habilitan cuando quede autorizada.</Notice>}
    {active && <>
      {!favorites.length && <Empty icon="pin" title="Sin lugares frecuentes" message="Busca abajo un lugar del directorio y márcalo: saldrá primero al asignar viajes y llevará sus notas privadas." />}
      {favorites.length > 0 && <div className="list">
        {favorites.map(p => <div className="item" key={p.id}>
          <div className="row between multiline">
            <div>
              <div className="title">{p.name}</div>
              <div className="small muted">{[p.zone, p.country].filter(Boolean).join(', ')}</div>
            </div>
            <div className="actions">
              <Btn small tone="secondary" busy={busy} onClick={() => setNotesFor(p)}>Notas</Btn>
              <Btn small tone="ghost" busy={busy} onClick={() => setFavorite(p.id, false)}>Quitar</Btn>
            </div>
          </div>
        </div>)}
      </div>}
      <Field label="Buscar en el directorio" value={query} onChange={e => setQuery(e.target.value)}
        placeholder="Nombre, zona o dirección…" hint="Marca como frecuentes los lugares que la empresa usa seguido." />
      {!!q && <div className="list">
        {results.map(p => {
          const fav = favoriteIds.includes(p.id);
          return <div className="item" key={p.id}>
            <div className="row between multiline">
              <div>
                <div className="title">{p.name}</div>
                <div className="small muted">{[p.zone, p.address].filter(Boolean).join(' · ')}</div>
              </div>
              <div className="actions">
                {fav && <Badge tone="ok">Frecuente</Badge>}
                <Btn small tone={fav ? 'ghost' : 'secondary'} busy={busy} onClick={() => setFavorite(p.id, !fav)}>{fav ? 'Quitar' : 'Marcar'}</Btn>
              </div>
            </div>
          </div>;
        })}
        {!results.length && <Empty title="Sin coincidencias" message="Prueba con otro nombre, zona o dirección." />}
      </div>}
    </>}
    {notesFor && <CompanyNotesModal place={notesFor} onClose={() => setNotesFor(null)} />}
  </Card>;
}

/** Notas privadas de la empresa sobre un lugar: lectura perezosa al abrir
    (listCompanyNotes) y recarga tras guardar/borrar. El backend deja borrar al
    autor o a la oficina — esta vista es de oficina, así que puede con todas. */
function CompanyNotesModal({ place, onClose }: { place: Place; onClose(): void }) {
  const { token, session, data, busy, mutate, authFail } = usePortal();
  const [notes, setNotes] = useState<CompanyNote[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [draft, setDraft] = useState('');
  const office = data?.company?.role === 'office';

  const load = async () => {
    try {
      const { notes } = await api<{ notes: CompanyNote[] }>('listCompanyNotes', { placeId: place.id }, token);
      setNotes(notes); setFailed(false);
    } catch (e) { if (authFail(e)) return; setNotes(null); setFailed(true); }
  };
  // `data` cambia de identidad tras cada mutate — recarga también las notas
  // perezosas (guardar/borrar una ya llama a load(), la dep lo refuerza).
  useEffect(() => { void load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [token, place.id, data]);

  const save = async () => {
    const body = draft.trim();
    if (!body) return;
    if (await mutate(() => api('saveCompanyNote', { placeId: place.id, body }, token), 'Nota privada guardada.')) { setDraft(''); void load(); }
  };
  const remove = async (note: CompanyNote) => {
    if (!confirm('¿Borrar esta nota privada? Dejará de verse para los de tu empresa.')) return;
    if (await mutate(() => api('deleteCompanyNote', { placeId: place.id, noteId: note.id }, token), 'Nota eliminada.')) void load();
  };

  return <Modal title={`Notas de ${place.name}`} onClose={onClose}>
    <Notice tone="info">Solo las ven los miembros de tu empresa: claves internas, contactos propios o avisos que no van al directorio.</Notice>
    {failed && <>
      <Notice tone="warning">No se pudieron cargar las notas privadas.</Notice>
      <Btn small tone="secondary" onClick={() => { setFailed(false); setNotes(null); void load(); }}>Reintentar</Btn>
    </>}
    {notes === null && !failed && <Skeleton rows={3} />}
    {notes !== null && <>
      {!notes.length && <Empty title="Sin notas privadas" message="Agrega abajo claves internas, contactos propios o avisos del equipo." />}
      <div className="list">
        {notes.map(note => {
          const own = note.authorId === session.uid;
          const edited = note.updatedAt !== note.createdAt;
          return <div className="item" key={note.id}>
            <div className="small">{note.body}</div>
            <div className="row between multiline">
              <span className="tiny muted">Por {own ? 'ti' : note.authorName || 'tu empresa'} · {dateTime(note.createdAt)}{edited ? ' · editada' : ''}</span>
              {(own || office) && <Btn small tone="danger" busy={busy} onClick={() => void remove(note)}>Borrar</Btn>}
            </div>
          </div>;
        })}
      </div>
      <TextArea label="Nueva nota privada" value={draft} onChange={e => setDraft(e.target.value)} maxLength={1000}
        placeholder="Clave interna, contacto de la empresa, aviso…" hint="La ven solo los miembros de tu empresa." />
      <div className="actions end modal-actions">
        <Btn busy={busy} disabled={!draft.trim()} onClick={() => void save()}>Guardar nota</Btn>
      </div>
    </>}
  </Modal>;
}

/** Fila compacta de viaje (lista de Viajes): chofer, ruta con paradas,
    avance, badge de estado y chevron — el detalle vive en la ventana.
    En viajes abiertos avisa de defectos graves sin resolver o de la salida
    ya autorizada por la oficina. */
function TripRow({ trip, placeName, defects, onOpen }: { trip: Trip; placeName(id: string): string; defects: { total: number; names: string[] }; onOpen(): void }) {
  const ts = TRIP_STATUS[trip.status] ?? { label: trip.status, tone: 'muted' as const };
  const total = trip.stops.length;
  const done = trip.stops.filter(s => s.progress?.status === 'done').length;
  const visiting = trip.stops.find(s => s.progress?.status === 'visiting');
  const open = trip.status === 'assigned' || trip.status === 'active';
  // Adjuntos del viaje (tripDocs): el conteo lo trae el viaje serializado.
  const docCount = trip.tripDocsCount ?? 0;
  return <button type="button" className="arow" onClick={onOpen}>
    <span className="acol">
      <span className="title">{trip.driverName || 'Chofer'}</span>
      <span className="small muted">{done} de {total} parada{total === 1 ? '' : 's'}{visiting ? ` · En ${placeName(visiting.placeId)} ahora` : ''}</span>
      {open && defects.total > 0 && !trip.defectOverride && <span className="tiny u-bad">{defects.total} defecto{defects.total === 1 ? '' : 's'} grave{defects.total === 1 ? '' : 's'} sin resolver — {defects.names.join(', ')}</span>}
      {open && defects.total > 0 && !trip.defectOverride && trip.defectAuthRequest && <span className="tiny u-warn">El chofer pide tu autorización para salir{trip.defectAuthRequest.at ? ` · ${timeAgo(trip.defectAuthRequest.at)}` : ''}</span>}
      {trip.defectOverride && <span className="tiny u-ok" title={trip.defectOverride.note || undefined}>Salida con defectos autorizada por {trip.defectOverride.byName || 'la oficina'}</span>}
      <span className="progress" role="progressbar" aria-valuemin={0} aria-valuemax={total} aria-valuenow={done} aria-label="Paradas visitadas">
        <span className="fill" style={{ width: `${total ? Math.round(done / total * 100) : 0}%` }} />
      </span>
    </span>
    <span className="route ellipsis">{trip.stops.map(s => placeName(s.placeId)).join(' → ') || 'Viaje'}</span>
    <span className="hstack">
      {docCount > 0 && <Badge tone="muted"><Icon name="doc" size={13} />{docCount} doc{docCount === 1 ? '' : 's'}</Badge>}
      <Badge tone={ts.tone}>{ts.label}</Badge>
    </span>
    <Icon name="chevronRight" size={16} />
  </button>;
}

/** Ventana del viaje: encabezado con chofer y estado, tira de datos
    (paradas, unidades, fechas), paradas con su avance, notas y acciones.
    Los viajes cerrados son de solo lectura — sin editar ni cancelar. */
function TripDetailModal({ trip, placeName, unitsText, defects, onEdit, onCancel, onAuthorize, onClose }: {
  trip: Trip | undefined; placeName(id: string): string; unitsText(trip: Trip): string;
  defects: { total: number; names: string[] };
  onEdit(trip: Trip): void; onCancel(trip: Trip): void; onAuthorize(trip: Trip): void; onClose(): void;
}) {
  const { busy } = usePortal();
  if (!trip) return <Modal title="Viaje" onClose={onClose}><Empty title="El viaje ya no existe" /></Modal>;
  const ts = TRIP_STATUS[trip.status] ?? { label: trip.status, tone: 'muted' as const };
  const total = trip.stops.length;
  const done = trip.stops.filter(s => s.progress?.status === 'done').length;
  const closed = trip.status === 'done' || trip.status === 'cancelled';
  const units = unitsText(trip);
  return <Modal title={trip.stops.map(s => placeName(s.placeId)).join(' → ') || 'Viaje'} onClose={onClose} wide>
    <div className="uhead-row">
      <span className="uicon lg"><Icon name="route" size={26} /></span>
      <div className="grow">
        <div className="title">{trip.driverName || 'Chofer'}</div>
        <div className="small muted">Asignado {dateTime(trip.createdAt)} por {trip.createdByName}{units ? ` · ${units}` : ''}</div>
      </div>
      <Badge tone={ts.tone}>{ts.label}</Badge>
    </div>

    <div className="ustats">
      <div className="ustat">
        <span className="ulabel">Paradas</span>
        <span className="uval">{done} de {total}</span>
        <span className="tiny muted">{done === total ? 'completadas' : 'visitadas'}</span>
      </div>
      <div className="ustat">
        <span className="ulabel">Unidades</span>
        <span className="uval">{units || '—'}</span>
        <span className="tiny muted">{units ? 'tracto + caja' : 'sin asignar'}</span>
      </div>
      <div className="ustat">
        <span className="ulabel">Actualizado</span>
        <span className="uval">{dateOnly(trip.updatedAt)}</span>
        <span className="tiny muted">{timeAgo(trip.updatedAt)}</span>
      </div>
    </div>

    {!closed && defects.total > 0 && !trip.defectOverride && <Notice tone="warning" icon="alert">
      {defects.total} defecto{defects.total === 1 ? '' : 's'} grave{defects.total === 1 ? '' : 's'} sin resolver en {defects.names.join(' y ')} — {trip.status === 'active'
        ? 'la unidad ya circula con ellos: resuélvelos o asienta tu autorización.'
        : 'el chofer no podrá iniciar el viaje hasta resolverlos o hasta que autorices la salida.'}
      {trip.defectAuthRequest && <> <b>{trip.defectAuthRequest.byName || 'El chofer'} pidió autorización{trip.defectAuthRequest.at ? ` ${timeAgo(trip.defectAuthRequest.at)}` : ''}.</b></>}
    </Notice>}
    {trip.defectOverride && <Notice tone="info" icon="check">
      Salida con defectos autorizada por {trip.defectOverride.byName || 'la oficina'}{trip.defectOverride.at ? ` · ${dateTime(trip.defectOverride.at)}` : ''}{trip.defectOverride.note ? ` — ${trip.defectOverride.note}` : ''}
    </Notice>}

    <h3 className="card-title sm">Paradas</h3>
    <TripStops stops={trip.stops} placeName={placeName} badge={tripProgressBadge(trip.stops)} />

    <TripDocsSection trip={trip} placeName={placeName} />

    {trip.notes && <>
      <h3 className="card-title sm">Notas</h3>
      <div className="small">{trip.notes}</div>
    </>}

    {!closed && <div className="actions end modal-actions">
      {defects.total > 0 && !trip.defectOverride && <Btn small tone="secondary" busy={busy} onClick={() => onAuthorize(trip)}>Autorizar salida con defectos</Btn>}
      <Btn small tone="secondary" busy={busy} onClick={() => onEdit(trip)}>Editar</Btn>
      <Btn small tone="danger" busy={busy} onClick={() => { onCancel(trip); onClose(); }}>Cancelar viaje</Btn>
    </div>}
  </Modal>;
}

/** Documentos del viaje (companies/<cid>/tripDocs): carta porte, factura,
    pedimento, POD… Lectura perezosa al abrir el viaje (listTripDocs) — el
    snapshot no los trae. La oficina adjunta y borra; el POD lo sube el chofer
    desde su app al entregar. */
function TripDocsSection({ trip, placeName }: { trip: Trip; placeName(id: string): string }) {
  const { token, busy, mutate, data, session, authFail } = usePortal();
  const isOffice = data?.company?.role === 'office' || session.role === 'admin';
  const [docs, setDocs] = useState<TripDoc[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [adding, setAdding] = useState(false);

  const load = async () => {
    try {
      const { docs } = await api<{ docs: TripDoc[] }>('listTripDocs', { tripId: trip.id }, token);
      setDocs(docs ?? []); setFailed(false);
    } catch (e) { if (authFail(e)) return; setDocs(null); setFailed(true); }
  };
  // `data` recarga tras mutaciones locales (adjuntar/quitar docs, editar el
  // viaje…): el snapshot nuevo cambia de identidad y la lista se refresca.
  useEffect(() => { void load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [token, trip.id, data]);

  const docName = (doc: TripDoc) =>
    (doc.label && doc.label !== doc.kind ? doc.label : '') || TRIP_DOC_KINDS[doc.kind] || doc.kind;

  const remove = (doc: TripDoc) => {
    if (!confirm(`¿Quitar «${docName(doc)}» del viaje?`)) return;
    void mutate(() => api('deleteTripDoc', { docId: doc.id }, token), 'Documento del viaje eliminado.')
      .then(ok => { if (ok) void load(); });
  };

  // Mientras listTripDocs responde, el conteo del viaje serializado sirve
  // de placeholder; una vez cargada la lista manda su largo real.
  const shownCount = docs === null ? (trip.tripDocsCount ?? 0) : docs.length;

  return <>
    <div className="row between multiline">
      <h3 className="card-title sm">Documentos del viaje{shownCount ? ` · ${shownCount}` : ''}</h3>
      {isOffice && <Btn small tone="secondary" onClick={() => setAdding(true)}>Adjuntar</Btn>}
    </div>
    {docs === null && !failed && <Skeleton rows={2} />}
    {/* Igual que el aviso del feed: sin role="status" al llevar un botón. */}
    {failed && <div className="notice warning"><span className="nbody">No se pudieron cargar los documentos. <Btn small tone="ghost" onClick={() => { setFailed(false); void load(); }}>Reintentar</Btn></span></div>}
    {docs !== null && !docs.length && <Empty icon="doc" title="Sin documentos"
      message="Adjunta carta porte, factura o pedimento; el comprobante de entrega (POD) lo sube el chofer desde la app." />}
    {docs !== null && docs.length > 0 && <div className="list">
      {docs.map(doc => {
        const kindLabel = TRIP_DOC_KINDS[doc.kind] ?? doc.kind;
        const name = docName(doc);
        return <div className="item" key={doc.id}>
        <div className="row between multiline">
          <div className="grow">
            <div className="title sm">{name} {doc.kind === 'pod' && <Badge tone="ok">POD</Badge>}{name !== kindLabel && <Badge tone="muted">{kindLabel}</Badge>}</div>
            <div className="tiny muted">{[
              doc.stopId ? `En ${placeName(doc.stopId)}` : '',
              doc.note,
              `Lo subió ${doc.uploadedByName || 'un miembro'} ${timeAgo(doc.uploadedAt)}`,
            ].filter(Boolean).join(' · ')}</div>
            {/* Miniatura por contentType (imagen) o botón «Ver PDF»: media-ref
                company/<cid>/tripdoc/<docId>/<attId> con Bearer. */}
            {doc.attachments.length > 0 && <div className="doc-atts">
              {doc.attachments.map(a => <TripDocAttachment key={a.id} docId={doc.id} att={a} />)}
            </div>}
          </div>
          {isOffice && <Btn small tone="ghost" busy={busy} onClick={() => remove(doc)}>Quitar</Btn>}
        </div>
      </div>;
      })}
    </div>}
    {adding && <TripDocForm trip={trip} onSaved={() => void load()} onClose={() => setAdding(false)} />}
  </>;
}

/** Adjuntar un documento al viaje (saveTripDoc): tipo, nombre, nota y hasta 4
    adjuntos (foto o PDF, el servicio exige ≥1). El POD no se ofrece en el
    selector — lo sube el chofer al entregar desde la app. */
function TripDocForm({ trip, onSaved, onClose }: { trip: Trip; onSaved(): void; onClose(): void }) {
  const { token, busy, error, mutate, dismiss, authFail, fail } = usePortal();
  const kinds = (Object.keys(TRIP_DOC_KINDS) as TripDocKind[]).filter(k => k !== 'pod');
  const [kind, setKind] = useState<TripDocKind>('cartaPorte');
  const [label, setLabel] = useState('');
  const [note, setNote] = useState('');
  // El prep de adjuntos va ANTES del mutate: sin bandera propia un doble clic
  // arrancaba el trabajo dos veces con `busy` aún apagado.
  const [submitting, setSubmitting] = useState(false);
  const atts = useDocFiles();

  const submit = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const files = await atts.payload();
      // `stopId` solo aplica al POD (la parada que acredita) — el backend
      // rechaza la llave con otro tipo, así que aquí no viaja. La etiqueta va
      // explícita siempre: si va vacía el servicio la rellena con la suya y la
      // fila la mostraría como texto distinto al tipo.
      const ok = await mutate(() => api('saveTripDoc', {
        input: {
          tripId: trip.id, kind,
          label: label.trim() || TRIP_DOC_KINDS[kind],
          ...(note.trim() ? { note: note.trim() } : {}),
        },
        files,
      }, token, 60000), 'Documento adjuntado al viaje.');
      if (ok) { onSaved(); onClose(); }
    } catch (e) {
      // Token muerto durante el prep → authFail saca a login; otro fallo del
      // prep se reporta al banner en vez de morir como unhandled rejection.
      if (!authFail(e)) fail(e);
    } finally { setSubmitting(false); }
  };
  const valid = atts.files.length > 0 && (kind !== 'otro' || label.trim().length > 0);

  return <Modal title="Adjuntar documento al viaje" onClose={onClose}>
    {error && <Notice tone="danger" onClose={dismiss}>{error}</Notice>}
    <div className="form-grid">
      <Select label="Tipo de documento" value={kind} onChange={e => setKind(e.target.value as TripDocKind)} autoFocus>
        {kinds.map(k => <option key={k} value={k}>{TRIP_DOC_KINDS[k]}</option>)}
      </Select>
      <Field label="Nombre visible" value={label} onChange={e => setLabel(e.target.value)} maxLength={120}
        placeholder={TRIP_DOC_KINDS[kind]} hint={kind === 'otro' ? 'Con «Otro» escribe el nombre del documento.' : 'Opcional — si queda vacío se usa el tipo.'} />
    </div>
    <TextArea label="Nota" value={note} onChange={e => setNote(e.target.value)} maxLength={160} placeholder="Opcional" />
    <DocFilesField st={atts} busy={busy} />
    <div className="actions end modal-actions">
      <Btn tone="ghost" onClick={onClose}>Cancelar</Btn>
      <Btn busy={busy} disabled={submitting || !valid} onClick={() => void submit()}>Adjuntar documento</Btn>
    </div>
  </Modal>;
}

/** Autorización de salida con defectos graves (authorizeTripDefects): queda
    registrada en el viaje — quién autorizó, cuándo y la nota — y el chofer
    puede arrancar aunque el reporte siga abierto. */
function AuthorizeDefectsModal({ trip, onClose }: { trip: Trip; onClose(): void }) {
  const { token, busy, mutate } = usePortal();
  const [note, setNote] = useState('');
  const submit = () => {
    void mutate(() => api('authorizeTripDefects', { tripId: trip.id, note: note.trim() }, token), 'Salida autorizada.')
      .then(ok => { if (ok) onClose(); });
  };
  return <Modal title="Autorizar salida con defectos" onClose={onClose}>
    <p className="small muted u-m0">El viaje de {trip.driverName || 'el chofer'} podrá iniciar aunque la unidad tenga defectos graves sin resolver. La autorización queda registrada con tu nombre y hora — el chofer la verá en su viaje.</p>
    <TextArea label="Nota de la autorización (opcional)" value={note} onChange={e => setNote(e.target.value)} maxLength={300}
      placeholder="Ej. Fuga menor de aire — pasa a taller al regreso del viaje…" />
    <div className="actions end modal-actions">
      <Btn tone="ghost" onClick={onClose}>Cancelar</Btn>
      <Btn busy={busy} onClick={submit}>Autorizar salida</Btn>
    </div>
  </Modal>;
}
